import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";

export default function SecurityPage() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/me/mfa");
    setStatus(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function run(fn, okMsg) {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  const start = () => run(async () => { const { data } = await api.post("/me/mfa/setup"); setSetup(data); setCode(""); });
  const enable = () => run(async () => { await api.post("/me/mfa/enable", { code }); setSetup(null); setCode(""); await load(); }, "Autenticazione a due fattori attivata");
  const disable = () => run(async () => { await api.post("/me/mfa/disable", { code }); setCode(""); await load(); }, "Autenticazione a due fattori disattivata");

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Account</div>
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">Sicurezza</h1>
      <Card className="rounded-2xl border-slate-100 shadow-soft p-6" data-testid="security-mfa-card">
        {!status ? <Loader2 className="w-5 h-5 animate-spin text-viva-500" /> : !status.feature_enabled ? (
          <div className="text-sm text-slate-600" data-testid="mfa-feature-disabled">
            L&apos;autenticazione a due fattori è temporaneamente disattivata sulla piattaforma.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              {status.enabled ? <ShieldCheck className="w-7 h-7 text-emerald-600" /> : <ShieldOff className="w-7 h-7 text-slate-400" />}
              <div>
                <div className="font-semibold text-slate-900">Autenticazione a due fattori (TOTP)</div>
                <div className="text-sm text-slate-500" data-testid="mfa-status-label">
                  {status.enabled ? "Attiva" : "Non attiva"} · {status.required ? "obbligatoria per il tuo ruolo" : "facoltativa per il tuo ruolo"}
                </div>
              </div>
            </div>
            {!status.enabled && !setup && (
              <Button onClick={start} disabled={busy} data-testid="mfa-start-setup" className="bg-viva-500 hover:bg-viva-600 text-white">Attiva 2FA</Button>
            )}
            {setup && (
              <div className="space-y-3" data-testid="mfa-setup-block">
                <p className="text-sm text-slate-600">Scansiona il codice con Google Authenticator e inserisci il codice a 6 cifre.</p>
                <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="Codice QR 2FA" className="w-44 h-44 border rounded-lg" />
                <code className="block text-xs font-mono break-all text-slate-700">{setup.secret}</code>
              </div>
            )}
            {(setup || (status.enabled && !status.required)) && (
              <div className="flex gap-2 mt-4 max-w-sm">
                <Input value={code} maxLength={6} inputMode="numeric" placeholder="123456" data-testid="security-mfa-code"
                       onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-10 font-mono tracking-widest" />
                {setup ? (
                  <Button onClick={enable} disabled={busy || code.length !== 6} data-testid="security-mfa-enable" className="bg-viva-500 hover:bg-viva-600 text-white">Conferma</Button>
                ) : (
                  <Button onClick={disable} disabled={busy || code.length !== 6} variant="outline" data-testid="security-mfa-disable" className="border-red-300 text-red-700">Disattiva</Button>
                )}
              </div>
            )}
            {status.enabled && status.required && (
              <p className="text-xs text-slate-500">Per il tuo ruolo la 2FA non può essere disattivata. In caso di smarrimento del dispositivo contatta l&apos;amministratore.</p>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
